import React, { useState } from "react";
import { TrendingUp, ShieldCheck, Sparkles, Loader2, CheckCircle2, Globe } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { logger } from "@/lib/logger";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

function toErrorMessage(err, fallback) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
  return typeof d === "object" && d.msg ? d.msg : String(d);
}

export default function Login() {
  const { t, i18n } = useTranslation();
  const { checkAuth } = useAuth();
  const currentLang = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  const toggleLang = () => i18n.changeLanguage(currentLang === "en" ? "es" : "en");

  const [mode, setMode] = useState("login"); // login | register | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await api.post("/auth/login", { email, password });
        // Session is now maintained via httpOnly cookie (secure). An earlier
        // code path shadowed the token in localStorage; no consumer reads it
        // anymore so we skip the write to reduce XSS surface.
        // Refresh /auth/me first so i18n picks up the server-stored language,
        // then show the toast in the correct language.
        await checkAuth();
        toast.success(t("auth.loginToast"));
        navigate("/dashboard");
      } else if (mode === "register") {
        await api.post("/auth/register", { email, password, name, language: currentLang });
        setRegistered(true);
        toast.success(t("auth.registerToast"));
      } else if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        toast.success(t("auth.forgotToast"));
        setMode("login");
      }
    } catch (err) {
      toast.error(toErrorMessage(err, t("common.error")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col md:flex-row relative">
      {/* Language quick toggle - visible top-right on login screen */}
      <button
        type="button"
        onClick={toggleLang}
        data-testid="login-lang-toggle"
        className="absolute top-4 right-4 z-20 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 border border-[#EBEBEA] text-xs font-semibold text-[#0A110E] hover:bg-white shadow-sm"
        title={t("lang.title")}
      >
        <Globe className="w-3.5 h-3.5" />
        <span className="uppercase tabular-nums">{currentLang === "en" ? "EN" : "ES"}</span>
      </button>

      {/* Left - hero */}
      <div className="hero-texture text-white md:w-1/2 flex flex-col justify-between p-10 md:p-16">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
            <TrendingUp className="w-5 h-5" />
          </div>
          <span className="font-display text-lg font-semibold tracking-tight">Structured Investments</span>
        </div>

        <div className="max-w-md fade-up">
          <p className="uppercase text-xs tracking-[0.2em] opacity-80 mb-4">{t("auth.heroPre")}</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05]">
            {t("auth.heroTitle")}
          </h1>
          <p className="mt-6 text-base opacity-90 leading-relaxed">
            {t("auth.heroDesc")}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4 max-w-md">
          <div className="flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 mt-1 opacity-90" />
            <div>
              <div className="text-sm font-semibold">{t("auth.featurePrivate")}</div>
              <div className="text-xs opacity-80">{t("auth.featurePrivateDesc")}</div>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 mt-1 opacity-90" />
            <div>
              <div className="text-sm font-semibold">{t("auth.featureLive")}</div>
              <div className="text-xs opacity-80">{t("auth.featureLiveDesc")}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right - action */}
      <div className="md:w-1/2 flex items-center justify-center p-10 md:p-16 bg-[#FAFAF9]">
        <div className="max-w-sm w-full fade-up">
          {registered ? (
            <div className="flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-full bg-[#1C7C54]/10 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-7 h-7 text-[#1C7C54]" />
              </div>
              <h2 className="font-display text-2xl font-semibold text-[#0A110E] mb-2">{t("auth.checkInbox")}</h2>
              <p className="text-sm text-[#5F7368] leading-relaxed mb-8">
                <Trans i18nKey="auth.verificationSent" values={{ email }} components={{ 1: <b /> }} />
              </p>
              <button
                type="button"
                data-testid="back-to-login-btn"
                onClick={() => { setRegistered(false); setMode("login"); setPassword(""); }}
                className="text-sm underline text-[#1C7C54]"
              >
                {t("auth.backToLoginShort")}
              </button>
            </div>
          ) : (
            <>
              {(() => {
                const copy = {
                  login: {
                    kicker: t("auth.welcome"),
                    title: t("auth.loginTitle"),
                    desc: t("auth.loginDesc"),
                  },
                  register: {
                    kicker: t("auth.createAccount"),
                    title: t("auth.registerTitle"),
                    desc: t("auth.registerDesc"),
                  },
                  forgot: {
                    kicker: t("auth.reset"),
                    title: t("auth.forgotTitle"),
                    desc: t("auth.forgotDesc"),
                  },
                }[mode];
                return (
                  <>
                    <p className="uppercase text-xs tracking-[0.2em] text-[#8A9992] mb-4">{copy.kicker}</p>
                    <h2 className="font-display text-3xl sm:text-4xl font-semibold text-[#0A110E] mb-3">{copy.title}</h2>
                    <p className="text-[#5F7368] mb-8 leading-relaxed">{copy.desc}</p>
                  </>
                );
              })()}

              <form onSubmit={submit} className="flex flex-col gap-3">
                {mode === "register" && (
                  <input
                    type="text"
                    placeholder={t("auth.namePlaceholder")}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="auth-name-input"
                    className="px-4 py-3 rounded-xl border border-[#EBEBEA] bg-white focus:outline-none focus:ring-2 focus:ring-[#1C7C54]/30"
                  />
                )}
                <input
                  type="email"
                  required
                  placeholder={t("auth.emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="auth-email-input"
                  className="px-4 py-3 rounded-xl border border-[#EBEBEA] bg-white focus:outline-none focus:ring-2 focus:ring-[#1C7C54]/30"
                />
                {mode !== "forgot" && (
                  <input
                    type="password"
                    required
                    minLength={8}
                    placeholder={mode === "register" ? t("auth.passwordMinPlaceholder") : t("auth.passwordPlaceholder")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="auth-password-input"
                    className="px-4 py-3 rounded-xl border border-[#EBEBEA] bg-white focus:outline-none focus:ring-2 focus:ring-[#1C7C54]/30"
                  />
                )}
                {mode === "login" && (
                  <button
                    type="button"
                    onClick={() => setMode("forgot")}
                    data-testid="auth-forgot-link"
                    className="self-end text-xs text-[#1C7C54] hover:underline"
                  >
                    {t("auth.forgotLink")}
                  </button>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  data-testid={`auth-submit-${mode}`}
                  className="mt-1 w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#0F291E] text-white font-semibold hover:bg-[#1C7C54] transition-colors duration-300 disabled:opacity-60"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  {{ login: t("auth.submitLogin"), register: t("auth.submitRegister"), forgot: t("auth.submitForgot") }[mode]}
                </button>
              </form>

              <div className="flex items-center gap-3 my-6 text-[10px] uppercase tracking-[0.2em] text-[#8A9992]">
                <div className="h-px bg-[#EBEBEA] flex-1" />
                <span>{t("common.or")}</span>
                <div className="h-px bg-[#EBEBEA] flex-1" />
              </div>

              <button
                type="button"
                data-testid="google-login-btn"
                onClick={startGoogleLogin}
                className="w-full flex items-center justify-center gap-3 px-6 py-3 rounded-xl border border-[#EBEBEA] bg-white text-[#0A110E] font-semibold hover:border-[#1C7C54]/40"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path fill="#0A110E" d="M12 10.2v3.9h5.5c-.24 1.4-1.68 4.1-5.5 4.1-3.3 0-6-2.75-6-6.15S8.7 5.9 12 5.9c1.9 0 3.15.8 3.88 1.5l2.63-2.55C16.84 3.35 14.6 2.5 12 2.5 6.75 2.5 2.5 6.75 2.5 12S6.75 21.5 12 21.5c6.9 0 9.5-4.85 9.5-7.3 0-.5-.06-.87-.15-1.25H12z" />
                </svg>
                {t("auth.googleBtn")}
              </button>

              <div className="mt-6 text-sm text-[#5F7368] text-center">
                {mode === "login" && (
                  <>{t("auth.noAccount")}{" "}
                    <button type="button" onClick={() => setMode("register")} data-testid="switch-register" className="text-[#1C7C54] hover:underline font-semibold">
                      {t("auth.createOne")}
                    </button>
                  </>
                )}
                {mode === "register" && (
                  <>{t("auth.haveAccount")}{" "}
                    <button type="button" onClick={() => setMode("login")} data-testid="switch-login" className="text-[#1C7C54] hover:underline font-semibold">
                      {t("auth.signIn")}
                    </button>
                  </>
                )}
                {mode === "forgot" && (
                  <button type="button" onClick={() => setMode("login")} data-testid="back-to-login-link" className="text-[#1C7C54] hover:underline">
                    {t("auth.backToLogin")}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
